/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase'
import { faBuilding } from '@fortawesome/free-solid-svg-icons/faBuilding'
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons/faFileInvoice'
import { faFolder } from '@fortawesome/free-solid-svg-icons/faFolder'
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome'
import { faTools } from '@fortawesome/free-solid-svg-icons/faTools'
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser'
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers'
import { faUsersCog } from '@fortawesome/free-solid-svg-icons/faUsersCog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormattedMessage, useIntl } from 'react-intl'
import RequirePerm from '../../../../modules/auth/components/RequirePerm'
import { INVOICE_SALE } from '../../../../modules/twh/enums/InvoiceTypes.mjs'
import NavLinkItem from './NavLinkItem'
import { faContactBook } from '@fortawesome/free-solid-svg-icons/faContactBook'
import { getCustomerListLink } from '../../../../modules/twh/lib/links/customerLinks'
import { getEstateListLink } from '../../../../modules/twh/lib/links/estateLinks'
import { getInterventionListLink } from '../../../../modules/twh/lib/links/interventionLinks'
import { getInvoiceListLink } from '../../../../modules/twh/lib/links/invoiceLinks'
import { getLeaseListLink } from '../../../../modules/twh/lib/links/leaseLinks'
import {
  getManagementReportListLink
} from '../../../../modules/twh/lib/links/managementReportLinks'
import { getMandateListLink } from '../../../../modules/twh/lib/links/mandateLinks'
import { getProspectingListLink } from '../../../../modules/twh/lib/links/prospectingLinks'
import { getTransactionListLink } from '../../../../modules/twh/lib/links/transactionLinks'
import { getAgendaLink } from '../../../../modules/twh/lib/links/agendaLinks'
import {
  getCompanySettingsLink,
  getTemplatesSettingsLink
} from '../../../../modules/twh/lib/links/settingsLinks'
import { getContactListLink } from '../../../../modules/twh/lib/links/contactLinks'
import { getRoleListLink } from '../../../../modules/role/lib/roleLinks'
import { getUserListLink } from '../../../../modules/user/lib/userLinks'
import { getPaymentListLink } from '../../../../modules/twh/lib/links/paymentLinks'
import { getDashboardLink } from '../../../../modules/twh/lib/links/dashboardLinks'
import { stringify } from 'qs'
import { getAgencyReportLink } from '../../../../modules/twh/lib/links/agencyReportLinks'
import {
  AppShell,
  AppShellSectionProps,
  Box,
  Button,
  NavLink,
  ScrollArea,
  Stack,
  Tooltip
} from '@mantine/core'
import { faFileContract } from '@fortawesome/free-solid-svg-icons/faFileContract'
import { faHouseCircleCheck } from '@fortawesome/free-solid-svg-icons/faHouseCircleCheck'
import { faCreditCard } from '@fortawesome/free-solid-svg-icons/faCreditCard'
import { faCalendar } from '@fortawesome/free-solid-svg-icons/faCalendar'
import { faFileAlt } from '@fortawesome/free-solid-svg-icons/faFileAlt'
import { memo } from 'react'
import {
  MANDATE_STATUS_ONGOING,
  MANDATE_STATUS_TO_COME_UP
} from '../../../../modules/twh/enums/MandateStatusCodes.mjs'
import {
  LEASE_STATUS_NOTICE,
  LEASE_STATUS_ONGOING,
  LEASE_STATUS_TO_COME_UP
} from '../../../../modules/twh/enums/LeaseStatusCodes.mjs'
import {
  INVOICE_STATUS_PENDING,
  INVOICE_STATUS_UNPAID
} from '../../../../modules/twh/enums/InvoiceStatusCodes.mjs'
import {
  TRANSACTION_STATUS_PENDING,
  TRANSACTION_STATUS_UNPAID
} from '../../../../modules/twh/enums/TransactionStatusCodes.mjs'
import {
  INTERVENTION_STATUS_ONGOING,
  INTERVENTION_STATUS_TO_COME_UP,
  INTERVENTION_STATUS_UNKNOWN
} from '../../../../modules/twh/enums/InterventionStatusCodes.mjs'
import { faDollarSign } from '@fortawesome/free-solid-svg-icons/faDollarSign'
import { faFileLines } from '@fortawesome/free-solid-svg-icons/faFileLines'
import { faChartSimple } from '@fortawesome/free-solid-svg-icons/faChartSimple'
import { faShield } from '@fortawesome/free-solid-svg-icons/faShield'
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog'
import { faSuitcase } from '@fortawesome/free-solid-svg-icons/faSuitcase'
import { faBoxArchive } from '@fortawesome/free-solid-svg-icons/faBoxArchive'
import { faCircleUp } from '@fortawesome/free-solid-svg-icons/faCircleUp'
import { faCircleDown } from '@fortawesome/free-solid-svg-icons/faCircleDown'
import AgendaEventsCountBadge
  from '../../../../modules/twh/components/agenda/AgendaEventsCountBadge'
import InvoiceCountBadge from '../../../../modules/twh/components/invoice/InvoiceCountBadge'
import { Link } from 'react-router-dom'
import InterventionCountBadge
  from '../../../../modules/twh/components/intervention/InterventionCountBadge'
import CreateMenu from '../../../../modules/twh/components/CreateMenu'

export type AdminNavbarProps = AppShellSectionProps

function AdminNavbar (props: AdminNavbarProps) {
  const intl = useIntl()
  return (
    <AppShell.Section p="sm" grow component={ScrollArea} {...props}>
      <Stack gap="sm">
        <CreateMenu width="target">
          <Button
            fullWidth
            variant="outline"
          >
            {intl.formatMessage({ id: 'new' })}
          </Button>
        </CreateMenu>

        <NavLinkItem
          to="/"
          leftSection={<FontAwesomeIcon fixedWidth icon={faHome} />}
          label={<FormattedMessage
            id="homepage"
            defaultMessage="Accueil"
          />}
        />

        <Box>
          <NavLink
            c="gray"
            childrenOffset={0}
            tt="uppercase"
            fw="bold"
            defaultOpened
            leftSection={<FontAwesomeIcon fixedWidth icon={faBuilding} />}
            label={intl.formatMessage({
              id: 'agency',
              defaultMessage: 'Agence'
            })}
          >
            <RequirePerm
              permissions={['read:dashboard']}
              render={() => (
                <NavLinkItem
                  to={getDashboardLink()}
                  leftSection={<FontAwesomeIcon fixedWidth icon={faChartSimple} />}
                  label={<FormattedMessage
                    id="dashboard"
                    defaultMessage="Panneau de bord"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:agency-report']}
              render={() => (
                <NavLinkItem
                  to={getAgencyReportLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faDollarSign}
                  />}
                  label={<FormattedMessage
                    id="agency_reports"
                    defaultMessage="Rapports de l'agence"
                  />}
                />
              )}
            />
          </NavLink>
        </Box>

        <Box>
          <NavLink
            c="gray"
            childrenOffset={0}
            tt="uppercase"
            fw="bold"
            defaultOpened
            leftSection={<FontAwesomeIcon fixedWidth icon={faSuitcase} />}
            label={intl.formatMessage({
              id: 'commercial',
              defaultMessage: 'Commercial'
            })}
          >
            <RequirePerm
              permissions={['read:customer', 'write:customer']}
              render={() => (
                <NavLinkItem
                  to={getCustomerListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faUser}
                  />}
                  label={<FormattedMessage
                    id="contacts"
                    defaultMessage="Contacts"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:mandate', 'write:mandate']}
              render={() => (
                <NavLinkItem
                  to={getMandateListLink({
                    status: [
                      MANDATE_STATUS_ONGOING,
                      MANDATE_STATUS_TO_COME_UP
                    ]
                  })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faFileContract}
                  />}
                  label={<FormattedMessage
                    id="mandates"
                    defaultMessage="Mandats"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:estate', 'write:estate']}
              render={() => (
                <NavLinkItem
                  to={getEstateListLink({ published: true })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faHome}
                  />}
                  label={<FormattedMessage
                    id="estates"
                    defaultMessage="Biens"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:prospecting', 'write:prospecting']}
              render={() => (
                <NavLinkItem
                  to={getProspectingListLink({ succeeded: 'null' })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faBriefcase}
                  />}
                  label={<FormattedMessage
                    id="prospecting.plural"
                    defaultMessage="Prospections"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:invoice', 'write:invoice']}
              render={() => (
                <NavLinkItem
                  to={getInvoiceListLink({ type: INVOICE_SALE })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faHouseCircleCheck}
                  />}
                  label={<FormattedMessage
                    id="sales"
                    defaultMessage="Ventes"
                  />}
                  rightSection={
                    <Tooltip
                      label={intl.formatMessage({
                        id: 'unpaid_invoices',
                        defaultMessage: 'Factures impayées'
                      })}
                      position="right"
                      withArrow
                      withinPortal
                    >
                      <Link
                        to={getInvoiceListLink({
                          status: [INVOICE_STATUS_UNPAID],
                          type: INVOICE_SALE
                        })}
                      >
                        <InvoiceCountBadge status="unpaid" type="sale" />
                      </Link>
                    </Tooltip>}
                />
              )}
            />
          </NavLink>
        </Box>

        <Box>
          <NavLink
            c="gray"
            childrenOffset={0}
            tt="uppercase"
            fw="bold"
            defaultOpened
            leftSection={<FontAwesomeIcon fixedWidth icon={faBoxArchive} />}
            label={intl.formatMessage({
              id: 'management',
              defaultMessage: 'Gestion'
            })}
          >
            <RequirePerm
              permissions={['read:lease', 'write:lease']}
              render={() => (
                <NavLinkItem
                  to={getLeaseListLink({
                    status: [
                      LEASE_STATUS_ONGOING,
                      LEASE_STATUS_NOTICE,
                      LEASE_STATUS_TO_COME_UP
                    ]
                  })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faFolder}
                  />}
                  label={<FormattedMessage
                    id="rental_files"
                    defaultMessage="Dossiers de location"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:invoice', 'write:invoice']}
              render={() => (
                <NavLinkItem
                  to={getInvoiceListLink({
                    status: [
                      INVOICE_STATUS_PENDING,
                      INVOICE_STATUS_UNPAID
                    ]
                  })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faFileInvoice}
                  />}
                  label={<FormattedMessage
                    id="invoices"
                    defaultMessage="Factures"
                  />}
                  rightSection={
                    <Tooltip
                      label={intl.formatMessage({
                        id: 'unpaid_invoices',
                        defaultMessage: 'Factures impayées'
                      })}
                      position="right"
                      withArrow
                      withinPortal
                    >
                      <Link
                        to={getInvoiceListLink({
                          status: [INVOICE_STATUS_UNPAID]
                        })}
                      >
                        <InvoiceCountBadge status="unpaid" />
                      </Link>
                    </Tooltip>}
                />
              )}
            />
            <RequirePerm
              permissions={['read:payment', 'write:payment']}
              render={() => (
                <NavLinkItem
                  to={getPaymentListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faCreditCard}
                  />}
                  label={<FormattedMessage
                    id="payments"
                    defaultMessage="Paiements"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:transaction', 'write:transaction']}
              render={() => (
                <NavLinkItem
                  to={getTransactionListLink(true) + `?${stringify({
                    status: [
                      TRANSACTION_STATUS_PENDING,
                      TRANSACTION_STATUS_UNPAID
                    ]
                  })}`}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faCircleUp}
                  />}
                  label={<FormattedMessage
                    id="expenses"
                    defaultMessage="Dépenses"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:transaction', 'write:transaction']}
              render={() => (
                <NavLinkItem
                  to={getTransactionListLink(false) + `?${stringify({
                    status: [
                      TRANSACTION_STATUS_PENDING,
                      TRANSACTION_STATUS_UNPAID
                    ]
                  })}`}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faCircleDown}
                  />}
                  label={<FormattedMessage
                    id="revenues"
                    defaultMessage="Revenus"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:management-report']}
              render={() => (
                <NavLinkItem
                  to={getManagementReportListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faFileLines}
                  />}
                  label={<FormattedMessage
                    id="management_reports"
                    defaultMessage="Rapports de gérance"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:intervention', 'write:intervention']}
              render={() => (
                <NavLinkItem
                  to={getInterventionListLink({
                    status: [
                      INTERVENTION_STATUS_ONGOING,
                      INTERVENTION_STATUS_TO_COME_UP,
                      INTERVENTION_STATUS_UNKNOWN
                    ]
                  })}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faTools}
                  />}
                  label={<FormattedMessage
                    id="interventions"
                    defaultMessage="Interventions"
                  />}
                  rightSection={
                    <Tooltip
                      label={intl.formatMessage({
                        id: 'unfinished_interventions',
                        defaultMessage: 'Interventions non terminées'
                      })}
                      position="right"
                      withArrow
                      withinPortal
                    >
                      <Link
                        to={getInterventionListLink({
                          status: [INTERVENTION_STATUS_UNKNOWN]
                        })}
                      >
                        <InterventionCountBadge status="unknown" />
                      </Link>
                    </Tooltip>}
                />
              )}
            />
            <RequirePerm
              permissions={['read:calendar']}
              render={() => (
                <NavLinkItem
                  rightSection={<AgendaEventsCountBadge />}
                  to={getAgendaLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faCalendar}
                  />}
                  label={<FormattedMessage
                    id="agenda"
                    defaultMessage="Agenda"
                  />}
                />
              )}
            />
          </NavLink>
        </Box>

        <Box>
          <NavLink
            c="gray"
            childrenOffset={0}
            tt="uppercase"
            fw="bold"
            // defaultOpened
            leftSection={<FontAwesomeIcon fixedWidth icon={faCog} />}
            label={intl.formatMessage({
              id: 'settings',
              defaultMessage: 'Paramètres'
            })}
          >
            <RequirePerm
              permissions={['read:contact', 'write:contact']}
              render={() => (
                <NavLinkItem
                  to={getContactListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faContactBook}
                  />}
                  label={<FormattedMessage
                    id="contacts"
                    defaultMessage="Contacts"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:setting', 'write:setting']}
              render={() => (
                <NavLinkItem
                  to={getTemplatesSettingsLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faFileAlt}
                  />}
                  label={<FormattedMessage
                    id="document_templates"
                    defaultMessage="Modèles de document"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:setting', 'write:setting']}
              render={() => (
                <NavLinkItem
                  to={getCompanySettingsLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faBuilding}
                  />}
                  label={<FormattedMessage
                    id="company"
                    defaultMessage="Société"
                  />}
                />
              )}
            />
          </NavLink>
        </Box>

        <Box>
          <NavLink
            c="gray"
            childrenOffset={0}
            fw="bold"
            tt="uppercase"
            // defaultOpened
            leftSection={<FontAwesomeIcon fixedWidth icon={faShield} />}
            label={intl.formatMessage({
              id: 'security',
              defaultMessage: 'Sécurité'
            })}
          >
            <RequirePerm
              permissions={['read:role', 'write:role']}
              render={() => (
                <NavLinkItem
                  to={getRoleListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faUsersCog}
                  />}
                  label={<FormattedMessage
                    id="roles"
                    defaultMessage="Rôles"
                  />}
                />
              )}
            />
            <RequirePerm
              permissions={['read:user', 'write:user']}
              render={() => (
                <NavLinkItem
                  to={getUserListLink()}
                  leftSection={<FontAwesomeIcon
                    fixedWidth
                    icon={faUsers}
                  />}
                  label={<FormattedMessage
                    id="users"
                    defaultMessage="Utilisateurs"
                  />}
                />
              )}
            />
          </NavLink>
        </Box>
      </Stack>
    </AppShell.Section>
  )
}

export default memo(AdminNavbar)
